// src/layouts/admin-v2/cardholders/api.js

import Api from "utils/api";
import { decryptJSON } from "global/security";
import { getFile } from "utils";

// ✅ V2 Base (because backend routes mounted like:
// /api/super_admin_v2/cardholders/...
const BASE_V2 = "/super_admin_v2/cardholders";

/**
 * ✅ Admin V2 - Get Cardholders list
 * GET /api/super_admin_v2/cardholders/getCardHolders
 * Returns: { data: <encrypted> }
 */
export const getCardholdersV2 = async () => {
  try {
    const { response, error } = await Api(`${BASE_V2}/getCardHolders`, "get");

    if (error) throw new Error(error?.message || "Failed to fetch cardholders");

    const decrypted = decryptJSON(response?.data);
    return Array.isArray(decrypted) ? decrypted : [];
  } catch (err) {
    console.error("getCardholdersV2 error:", err);
    return [];
  }
};

export const getCardholderDetailV2 = async (_id) => {
  try {
    const { response, error } = await Api(`${BASE_V2}/getCardHolder/${_id}`, "get");

    if (error) throw new Error(error?.message || "Failed to fetch cardholder detail");

    return decryptJSON(response?.data);
  } catch (err) {
    console.error("getCardholderDetailV2 error:", err);
    throw err;
  }
};

/**
 * ✅ Admin V2 - CREATE Cardholder (with images)
 * POST /api/super_admin_v2/cardholders/createCardHolder
 * multipart/form-data
 */
export const createCardholderV2 = async (form) => {
  try {
    const formData = new FormData();

    // ✅ images
    formData.append("logoImage", getFile(form.logoImage));
    formData.append("stripImage", getFile(form.stripImage));

    // ✅ basic fields
    formData.append("backgroundColor", form.backgroundColor || "#2C3255");
    formData.append("firstName", form.firstName || "");
    formData.append("lastName", form.lastName || "");
    formData.append("organizationName", form.organisationName || "");
    formData.append("jobTitle", form.jobTitle || "");
    formData.append("prefix", form.prefix || "");

    // ✅ arrays from CreatePass component
    const emails = Array.isArray(form.email) ? form.email : [];
    const phones = Array.isArray(form.phone) ? form.phone : [];
    const addresses = Array.isArray(form.addresses)
      ? form.addresses.map((a) => a.value)
      : [];

    formData.append("emails", JSON.stringify(emails));
    formData.append("phones", JSON.stringify(phones));
    formData.append("addresses", JSON.stringify(addresses));

    // ✅ urls
    formData.append("companyURL", form.companyURL || "");
    formData.append("linkedInURL", form.linkedInURL || "");

    const { response, error } = await Api(`${BASE_V2}/createCardHolder`, "post", {
      payload: formData,
    });

    if (error) throw new Error(error?.message || "Create failed");
    return response;
  } catch (err) {
    console.error("createCardholderV2 error:", err);
    throw err;
  }
};

/**
 * ✅ Admin V2 - UPDATE Cardholder
 * If image changed => updateCardHolder (FormData)
 * Else => updateCardHolderWithoutImages (JSON)
 */
export const updateCardholderV2 = async (form, state) => {
  try {
    const hasNewLogo = Boolean(form?.logoImage && form?.logoImageName);
    const hasNewStrip = Boolean(form?.stripImage && form?.stripImageName);

    const emails = Array.isArray(form.email) ? form.email : [];
    const phones = Array.isArray(form.phone) ? form.phone : [];
    const addresses = Array.isArray(form.addresses)
      ? form.addresses.map((a) => a.value)
      : [];

    // ✅ If image changed => FormData route
    if (hasNewLogo || hasNewStrip) {
      const formData = new FormData();

      formData.append("_id", state?._id);

      formData.append("logoImage", getFile(form.logoImage));
      formData.append("stripImage", getFile(form.stripImage));

      formData.append("backgroundColor", form.backgroundColor || "#2C3255");
      formData.append("firstName", form.firstName || "");
      formData.append("lastName", form.lastName || "");
      formData.append("organizationName", form.organisationName || "");
      formData.append("jobTitle", form.jobTitle || "");
      formData.append("prefix", form.prefix || "");

      formData.append("emails", JSON.stringify(emails));
      formData.append("phones", JSON.stringify(phones));
      formData.append("addresses", JSON.stringify(addresses));

      formData.append("companyURL", form.companyURL || "");
      formData.append("linkedInURL", form.linkedInURL || "");

      const { response, error } = await Api(`${BASE_V2}/updateCardHolder`, "post", {
        payload: formData,
      });

      if (error) throw new Error(error?.message || "Update failed");
      return response;
    }

    // ✅ No image changed => JSON route
    const payload = {
      _id: state?._id,
      backgroundColor: form.backgroundColor || "#2C3255",
      firstName: form.firstName || "",
      lastName: form.lastName || "",
      organizationName: form.organisationName || "",
      jobTitle: form.jobTitle || "",
      prefix: form.prefix || "",

      // backend expects these as JSON strings (we parse JSON on backend)
      emails: JSON.stringify(emails),
      phones: JSON.stringify(phones),
      addresses: JSON.stringify(addresses),

      companyURL: form.companyURL || "",
      linkedInURL: form.linkedInURL || "",
    };

    const { response, error } = await Api(
      `${BASE_V2}/updateCardHolderWithoutImages`,
      "post",
      { payload }
    );

    if (error) throw new Error(error?.message || "Update failed");
    return response;
  } catch (err) {
    console.error("updateCardholderV2 error:", err);
    throw err;
  }
};

/**
 * ✅ Admin V2 - Disable/Enable/Delete/Resend Invite
 * These are "full control" actions for Admin2.0
 */

export const disableCardholderV2 = async (_id) => {
  const { response, error } = await Api(`${BASE_V2}/disableCardHolder`, "post", {
    payload: { _id },
  });
  if (error) throw new Error(error?.message || "Disable failed");
  return response;
};

export const enableCardholderV2 = async (_id) => {
  const { response, error } = await Api(`${BASE_V2}/enableCardHolder`, "post", {
    payload: { _id },
  });
  if (error) throw new Error(error?.message || "Enable failed");
  return response;
};

export const deleteCardholderV2 = async (_id) => {
  const { response, error } = await Api(`${BASE_V2}/deleteCardHolder`, "post", {
    payload: { _id },
  });
  if (error) throw new Error(error?.message || "Delete failed");
  return response;
};

export const resendInviteV2 = async (_id) => {
  const { response, error } = await Api(`${BASE_V2}/resendCardHolderInvite`, "post", {
    payload: { _id },
  });
  if (error) throw new Error(error?.message || "Resend failed");
  return response;
};
